/**
 *
 * Accessibility
 *
 */
import React, { memo } from "react";
import { FeaturesTable } from "./components/features-table";
import { Footer } from "app/components/AdminLayout/Footer";

interface Props {}

export const Accessibility = memo((props: Props) => {
  return (
    <React.Fragment>
      <main className="overflow-hidden">
        <section className="py-12 sm:py-14 md:py-16 lg:py-20 2xl:py-[6.5rem]">
          <div className="w-full max-w-[66.5rem] mx-auto">
            <div className="px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12 mb-3 sm:mb-4 lg:mb-5">
              <h1 className="uppercase font-mobile text-[#1D1D1B] text-[clamp(1.5625rem,calc(2.35vw+0.5rem),2.75rem)] font-medium leading-tight md:leading-snug lg:leading-[3.375rem]">
                Our Commitment to Accessibility
              </h1>
              <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mt-1.5 sm:mt-2 lg:mt-2.5 mb-1 sm:mb-1.5 lg:mb-2">
                VLE Mobile is committed to making our service and network
                accessible to you and family of customers. If you have questions
                about the accessibility of VLE Mobile's service, or have
                feedback on or experience technical difficulties using our
                network and services, or if you'd like to request accommodation
                with respect to our network, services, device or hardware
                compatibility, please contact us at support@vlemobile.com. We
                are always working to improve accessibility and ensure that VLE
                Mobile's network and services are designed for the greatest
                number of people, including those with disabilities.
              </p>
              <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7">
                Additionally, while we do not currently sell or distribute any
                devices, we are strongly encouraged customers of telephone
                digital carriers to consider devices that are accessible to
                one's family.
              </p>
            </div>
            <div className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5">
              {/* 1. Working Together to Improve Accessibility */}
              <div className="px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12">
                <h2 className="font-mobile text-[#1D1D1B] text-[1.375rem] sm:text-2xl md:text-[1.75rem] lg:text-3xl 2xl:text-[2rem] font-medium leading-[1.625rem] sm:leading-7 md:leading-8 lg:leading-9 2xl:leading-10 mb-1.5 sm:mb-2 lg:mb-2.5">
                  Working Together to Improve Accessibility
                </h2>
                <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mb-1 sm:mb-1.5 lg:mb-2">
                  Like all Americans, people with disabilities rely on
                  communication services in ways to remain active and informed
                  in daily life. The Communications and Accessibility Act of
                  2010 (CVAA) requires wireless services to be accessible for
                  people with disabilities. VLE Mobile is committed to ensuring
                  our services meet these standards.
                </p>
                <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7">
                  As a Mobile Virtual Network Operator (MVNO), VLE Mobile
                  partners with AT&T to provide voice, SMS, and data services on
                  their network infrastructure. Our multi-network plans also
                  leverage roaming agreements with major carriers nationally and
                  internationally. We work closely with our network partners to
                  ensure that all accessibility features supported by AT&T's
                  network are available to our customers, and we comply with all
                  FCC and state accessibility standards noted in 47 U.S.C.§§
                  255, 716, 718; 47 CFR §§6, 14; and other relevant standards.
                </p>
              </div>

              {/* 2. What We Do */}
              <div className="px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12">
                <h2 className="font-mobile text-[#1D1D1B] text-[1.375rem] sm:text-2xl md:text-[1.75rem] lg:text-3xl 2xl:text-[2rem] font-medium leading-[1.625rem] sm:leading-7 md:leading-8 lg:leading-9 2xl:leading-10 mb-1.5 sm:mb-2 lg:mb-2.5">
                  What We Do
                </h2>
                <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mb-1.5 sm:mb-2 lg:mb-2.5">
                  We design our customer platform and service offerings with
                  accessibility in mind, ensuring compatibility with AT&T's
                  network features and meeting ADA Web Accessibility Content
                  Guidelines Level AA standards where applicable.
                </p>
                <div className="">
                  <h3 className="font-mobile text-[#1D1D1B] text-lg sm:text-xl lg:text-[1.375rem] 2xl:text-2xl font-medium leading-[1.375rem] sm:leading-6 lg:leading-7 2xl:leading-8 mb-1 sm:mb-1.5 lg:mb-2">
                    Our SIM/eSIM Service
                  </h3>
                  <ul className="list-none space-y-0.5 sm:space-y-1 lg:space-y-1.5 text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7">
                    <li className="">
                      <strong>Bring Your Own Device (BYOD)</strong> - We provide
                      SIM/eSIM cards that work with all unlocked,
                      carrier-compatible mobile phones on AT&T's 4G and 5G
                      network. Our Triple Network plans utilize AT&T's primary
                      network along with roaming agreements on other major U.S.
                      carriers, giving you flexible nationwide coverage.
                    </li>
                    <li className="">
                      <strong>Hearing Aid Compatible Devices</strong> - While
                      VLE Mobile does not sell phones directly, AT&T's network
                      fully supports Hearing Aid Compatible (HAC) rated devices.
                      Customers with hearing or speech disabilities can choose
                      HAC-compatible devices from their preferred retailers and
                      use them seamlessly with VLE Mobile service on the AT&T
                      network.
                    </li>
                    <li className="">
                      <strong>Real-Time Text Service (RTT)</strong> - VLE Mobile
                      supports real-time text communication (RTT) through AT&T's
                      network infrastructure, providing accessible text-based
                      communication for customers with hearing or speech
                      disabilities.
                    </li>
                  </ul>
                </div>
              </div>

              {/* 3. Network Accessibility Services */}
              <div className="px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12">
                <h2 className="font-mobile text-[#1D1D1B] text-[1.375rem] sm:text-2xl md:text-[1.75rem] lg:text-3xl 2xl:text-[2rem] font-medium leading-[1.625rem] sm:leading-7 md:leading-8 lg:leading-9 2xl:leading-10 mb-1.5 sm:mb-2 lg:mb-2.5">
                  Network Accessibility Services
                </h2>
                <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mb-1 sm:mb-1.5 lg:mb-2">
                  As a Mobile Virtual Network Operator (MVNO) partnering with
                  AT&T, VLE Mobile customers benefit from all accessibility
                  features available on AT&T's network infrastructure. Our
                  multi-network plans also provide access to accessibility
                  services through our national and international roaming
                  agreements.
                </p>
                <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mb-1 sm:mb-1.5 lg:mb-2">
                  AT&T's network provides robust accessibility support including
                  Hearing Aid Compatible (HAC) services, Voice over LTE (VoLTE),
                  Wi-Fi Calling, and Real-Time Text (RTT). These services are
                  available to all VLE Mobile customers using compatible
                  devices. Our service maintains over 99.7% network uptime
                  through AT&T's infrastructure.
                </p>
                <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mb-1 sm:mb-1.5 lg:mb-2">
                  Should you have any questions about accessibility features
                  available through our network service, our customer support
                  team is available to assist you. We work directly with AT&T to
                  ensure that all accessibility requirements and FCC guidelines
                  are met, including VoLTE, HAC ratings, and E-911 capabilities.
                </p>
                <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mb-1 sm:mb-1.5 lg:mb-2">
                  Our customer support team is available 24/7 through multiple
                  accessible channels: phone, email, chat, SMS, and TTY/TDD
                  relay services (711). We're committed to helping customers
                  with disabilities access all the features and services
                  available through our AT&T network partnership.
                </p>
              </div>

              {/* 4. Customer Support Accessibility Services */}
              <div className="px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12">
                <h2 className="font-mobile text-[#1D1D1B] text-[1.375rem] sm:text-2xl md:text-[1.75rem] lg:text-3xl 2xl:text-[2rem] font-medium leading-[1.625rem] sm:leading-7 md:leading-8 lg:leading-9 2xl:leading-10 mb-1.5 sm:mb-2 lg:mb-2.5">
                  Customer Support Accessibility Services
                </h2>
                <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mb-1 sm:mb-1.5 lg:mb-2">
                  For telephone information regarding line offering or VLE TN
                  database, visit the FCC rules at:{" "}
                  <a
                    href="https://www.fcc.gov/general/disability-rights-office"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-[#FE4D01]"
                  >
                    https://www.fcc.gov/general/disability-rights-office
                  </a>
                </p>
                <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mb-1 sm:mb-1.5 lg:mb-2">
                  For the FCC and additional information about our E-911 with
                  and other HAC and VoLTE guidelines, visit the E-911 services
                  and resources at:{" "}
                  <a
                    href="https://www.fcc.gov/general/9-1-1-and-e9-1-1-services"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-[#FE4D01]"
                  >
                    https://www.fcc.gov/general/9-1-1-and-e9-1-1-services
                  </a>
                </p>
                <div className="">
                  <h6 className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mb-1 sm:mb-1.5 lg:mb-2">
                    VLE Mobile customers have access to a comprehensive range of
                    accessibility features through AT&T's network infrastructure
                    and our roaming partners:
                  </h6>
                </div>
              </div>

              {/* 5. Accessibility Features by Device Type */}
              <div className="">
                <h2 className="font-mobile text-[#1D1D1B] text-[1.375rem] sm:text-2xl md:text-[1.75rem] lg:text-3xl 2xl:text-[2rem] font-medium leading-[1.625rem] sm:leading-7 md:leading-8 lg:leading-9 2xl:leading-10 px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12 mb-1.5 sm:mb-2 lg:mb-2.5">
                  Accessibility Features by Device Type
                </h2>
                <FeaturesTable />
                <span className="inline-block text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base italic font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12">
                  Specific features depend on your device model and operating
                  system version. Please check your device manufacturer's
                  specifications for detailed accessibility features.
                </span>
              </div>

              {/* 6. How to Contact Us */}
              <div className="px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12">
                <h2 className="font-mobile text-[#1D1D1B] text-[1.375rem] sm:text-2xl md:text-[1.75rem] lg:text-3xl 2xl:text-[2rem] font-medium leading-[1.625rem] sm:leading-7 md:leading-8 lg:leading-9 2xl:leading-10 mb-1.5 sm:mb-2 lg:mb-2.5">
                  How to Contact Us
                </h2>
                <div className="space-y-3 sm:space-y-3.5 lg:space-y-4">
                  <div className="">
                    <h6 className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mb-1 sm:mb-1.5 lg:mb-2">
                      Have a VLE Mobile query or need information on
                      accessibility features specific to your device?
                    </h6>
                    <ul className="list-none space-y-0.5 sm:space-y-1 lg:space-y-1.5 text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7">
                      <li className="">
                        <strong>Email:</strong>{" "}
                        <a
                          href="mailto:support@vlemobile.com"
                          className="hover:underline"
                        >
                          support@vlemobile.com
                        </a>
                      </li>
                      <li className="">
                        <strong>Phone:</strong>{" "}
                        <a href="tel:18448536624" className="hover:underline">
                          1-844-VLE-MOBILE
                        </a>
                      </li>
                      <li className="">
                        <strong>TTY/TDD:</strong> 711 (Relay Services)
                      </li>
                      <li className="">
                        <strong>Live Chat:</strong> Available 24/7 on
                        vlemobile.com
                      </li>
                      <li className="">
                        <strong>SMS Text Support:</strong> Text "HELP" to 611
                        (provided by AT&T)
                      </li>
                    </ul>
                  </div>
                  <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7">
                    We're committed to responding to all accessibility inquiries
                    within 48 hours and working with you to ensure you have the
                    best possible experience with VLE Mobile's network services.
                  </p>
                </div>
              </div>

              <hr className="border-t border-[#1D1D1B66] mx-5 sm:mx-6 md:mx-7 lg:mx-9 xl:mx-12" />

              <div className="px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12">
                <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base italic font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7 mb-1 sm:mb-1.5 lg:mb-2">
                  VLE Mobile is continuously working to improve accessibility
                  across our network and customer service channels. This
                  commitment reflects our belief that everyone deserves access
                  to reliable, affordable mobile connectivity.
                </p>
                <span className="inline-block text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base italic font-normal leading-[1.3125rem] sm:leading-6 lg:leading-7">
                  Last updated: October 2025
                </span>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </React.Fragment>
  );
});
